import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
  Users, TrendingUp, DollarSign, Activity, ArrowUpRight, ArrowDownRight,
  UserPlus, Ticket, Star, Award, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlatformAdminDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/platform-admin/dashboard-full`, { headers: getAuthHeaders() });
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, [getAuthHeaders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const mrrGrowth = 12.5; // Mock growth percentage

  return (
    <div className="space-y-6" data-testid="platform-admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Platform Dashboard</h1>
          <p className="text-muted-foreground">Meybra Coach Platform Overview</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Activity className="text-green-400" size={16} />
          <span>All systems operational</span>
        </div>
      </div>

      {/* MRR & Revenue Section */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30 p-6 rounded-sm col-span-1 md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-green-400 uppercase tracking-wider">Monthly Recurring Revenue</span>
            <div className="flex items-center gap-1 text-green-400 text-sm">
              <ArrowUpRight size={14} />
              <span>+{mrrGrowth}%</span>
            </div>
          </div>
          <p className="font-heading text-4xl text-green-400">${data?.revenue?.total_mrr?.toLocaleString() || 0}</p>
          <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Coach Subs</p>
              <p className="text-lg font-semibold">${data?.revenue?.coach_mrr?.toLocaleString() || 0}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Athlete Pass</p>
              <p className="text-lg font-semibold">${data?.revenue?.pass_mrr?.toLocaleString() || 0}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Pass Plus</p>
              <p className="text-lg font-semibold">${data?.revenue?.plus_mrr?.toLocaleString() || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border/40 p-6 rounded-sm">
          <Ticket className="text-primary mb-2" size={24} />
          <p className="text-sm text-muted-foreground">Active Passes</p>
          <p className="font-heading text-3xl">{data?.revenue?.active_passes || 0}</p>
          <div className="flex items-center gap-2 mt-2 text-sm">
            <Star className="text-primary" size={14} />
            <span>{data?.revenue?.plus_passes || 0} Plus</span>
          </div>
        </div>

        <div className="bg-card border border-border/40 p-6 rounded-sm">
          <Award className="text-amber-400 mb-2" size={24} />
          <p className="text-sm text-muted-foreground">Coach Advantage</p>
          <p className="font-heading text-3xl">{data?.revenue?.advantage_coaches || 0}</p>
          <p className="text-xs text-muted-foreground mt-2">Free with 10+ athletes</p>
        </div>
      </div>

      {/* User Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <Users className="text-blue-400 mb-2" size={20} />
          <p className="text-2xl font-heading">{data?.users?.total_coaches || 0}</p>
          <p className="text-xs text-muted-foreground uppercase">Total Coaches</p>
        </div>
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <UserPlus className="text-green-400 mb-2" size={20} />
          <p className="text-2xl font-heading">{data?.users?.owner_coaches || 0}</p>
          <p className="text-xs text-muted-foreground uppercase">Subscribed</p>
        </div>
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <Users className="text-amber-400 mb-2" size={20} />
          <p className="text-2xl font-heading">{data?.users?.invited_coaches || 0}</p>
          <p className="text-xs text-muted-foreground uppercase">Invited Only</p>
        </div>
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <Users className="text-purple-400 mb-2" size={20} />
          <p className="text-2xl font-heading">{data?.users?.total_athletes || 0}</p>
          <p className="text-xs text-muted-foreground uppercase">Athletes</p>
        </div>
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <Users className="text-cyan-400 mb-2" size={20} />
          <p className="text-2xl font-heading">{data?.users?.total_parents || 0}</p>
          <p className="text-xs text-muted-foreground uppercase">Parents</p>
        </div>
      </div>

      {/* Acquisition & Activity */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* User Acquisition */}
        <div className="bg-card border border-border/40 p-6 rounded-sm">
          <h2 className="font-heading text-lg uppercase mb-4">User Acquisition</h2>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-muted-foreground">Coach-Led</span>
                <span className="font-semibold">{data?.acquisition?.coach_led || 0}</span>
              </div>
              <div className="w-full h-2 bg-muted/50 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-400"
                  style={{ 
                    width: `${data?.acquisition?.coach_led && data?.acquisition?.athlete_led 
                      ? (data.acquisition.coach_led / (data.acquisition.coach_led + data.acquisition.athlete_led)) * 100 
                      : 50}%` 
                  }}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-muted-foreground">Athlete-Led</span>
                <span className="font-semibold">{data?.acquisition?.athlete_led || 0}</span>
              </div>
              <div className="w-full h-2 bg-muted/50 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-400"
                  style={{ 
                    width: `${data?.acquisition?.coach_led && data?.acquisition?.athlete_led 
                      ? (data.acquisition.athlete_led / (data.acquisition.coach_led + data.acquisition.athlete_led)) * 100 
                      : 50}%` 
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Activity Metrics */}
        <div className="bg-card border border-border/40 p-6 rounded-sm">
          <h2 className="font-heading text-lg uppercase mb-4">Activity Metrics</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-muted/30 rounded-sm text-center">
              <p className="text-2xl font-heading text-primary">{data?.activity?.sessions_7d || 0}</p>
              <p className="text-xs text-muted-foreground">Sessions (7d)</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-sm text-center">
              <p className="text-2xl font-heading">{data?.activity?.sessions_30d || 0}</p>
              <p className="text-xs text-muted-foreground">Sessions (30d)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Coach Transitions */}
      <div className="bg-card border border-border/40 p-6 rounded-sm">
        <div className="flex items-center gap-2 mb-4">
          <RefreshCw className="text-primary" size={20} />
          <h2 className="font-heading text-lg uppercase">Coach Transitions</h2>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-muted/30 rounded-sm text-center">
            <p className="text-2xl font-heading">{data?.transitions?.total || 0}</p>
            <p className="text-xs text-muted-foreground">Total Transitions</p>
          </div>
          <div className="p-4 bg-muted/30 rounded-sm text-center">
            <p className="text-2xl font-heading text-amber-400">{data?.transitions?.last_30d || 0}</p>
            <p className="text-xs text-muted-foreground">Last 30 Days</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformAdminDashboard;
